import { z } from 'zod';

export const ARTIFACT_LIMITS = Object.freeze({
  bytes: 512 * 1024,
  nodes: 10000,
  depth: 48,
  text: 32000,
  cells: 10000,
  sheets: 32,
  formulaDepth: 32,
});
export const id = z
  .string()
  .min(1)
  .max(200)
  .refine((s) => !/[\u0000-\u0020\u007f]/.test(s));
export const digest = z.string().regex(/^[a-f0-9]{64}$/);
export const text = (max = ARTIFACT_LIMITS.text) =>
  z
    .string()
    .max(max)
    .refine((s) => !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(s));
export const generation = z
  .number()
  .int()
  .nonnegative()
  .max(Number.MAX_SAFE_INTEGER - 1);
export const instant = z.string().datetime({ offset: true });
export const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((s) => {
    const parsed = new Date(`${s}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === s;
  });
export const versionRefSchema = z
  .object({ artifactId: id, revisionId: id, payloadDigest: digest })
  .strict();

export function unique(values, ctx, path = []) {
  if (new Set(values).size !== values.length)
    ctx.addIssue({ code: 'custom', path, message: 'Duplicate stable identity' });
}

// Inspect before recursive schemas/stringification. Cycles, non-JSON values,
// excessive depth and oversized graphs cannot reach the parser or digest code.
function isJsonContainer(value) {
  if (value === null || ['string', 'boolean'].includes(typeof value)) return false;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new TypeError('invalid_artifact_json');
    return false;
  }
  if (typeof value !== 'object') throw new TypeError('invalid_artifact_json');
  if (!Array.isArray(value) && ![Object.prototype, null].includes(Object.getPrototypeOf(value)))
    throw new TypeError('invalid_artifact_json');
  return true;
}
export function assertJsonBounds(value, limits = ARTIFACT_LIMITS) {
  const stack = [{ value, depth: 0 }];
  const ancestors = new WeakSet();
  let count = 0;
  while (stack.length) {
    const item = stack.pop();
    if (item.exit) {
      ancestors.delete(item.value);
      continue;
    }
    if (++count > limits.nodes * 20 || item.depth > limits.depth)
      throw new TypeError('artifact_limits_exceeded');
    const v = item.value;
    if (!isJsonContainer(v)) continue;
    if (ancestors.has(v)) throw new TypeError('invalid_artifact_json');
    ancestors.add(v);
    stack.push({ value: v, exit: true });
    for (const entry of Object.values(v)) stack.push({ value: entry, depth: item.depth + 1 });
  }
  if (new TextEncoder().encode(JSON.stringify(value)).byteLength > limits.bytes)
    throw new TypeError('artifact_limits_exceeded');
}

export function boundedSchema(schema) {
  return z
    .unknown()
    .superRefine((value, ctx) => {
      try {
        assertJsonBounds(value);
      } catch (error) {
        ctx.addIssue({ code: 'custom', message: error.message });
      }
    })
    .pipe(schema);
}
